import { calculateEdrDifference, calculateLineupAverageEdr } from './edr';
import { normalizeCompatibility, partnerPreferenceForPair, type PartnerCompatibility, type PartnerPreferenceLike } from './partnerPreferences';
import type { ParticipationReportRow } from './participationReport';

export const LINEUP_HEALTH_WEIGHTS = {
  strength: 0.30,
  fairness: 0.25,
  partnerCompatibility: 0.20,
  availability: 0.15,
  scheduling: 0.10,
} as const;

export const LINEUP_HEALTH_COLLAPSE_STORAGE_PREFIX = 'courtcaptain:lineup-health-collapsed';

export type LineupHealthRating = 'Excellent' | 'Good' | 'Needs Attention' | 'At Risk';
export type LineupHealthSeverity = 'positive' | 'info' | 'warning' | 'error';

export type LineupHealthPlayer = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  estimated_dynamic_rating?: number | string | null;
  availability_status?: string | null;
};

export type LineupHealthPosition = {
  id: string;
  position_code: string;
  line_type?: string | null;
  required_player_count: number;
};

export type LineupHealthEntry = {
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};

export type LineupHealthConflict = {
  player_id: string;
  opponent_name?: string | null;
};

export type LineupHealthLineStatus = Record<string, string>;

export type LineupHealthLineCompatibility = {
  positionCode: string;
  compatibility: PartnerCompatibility;
  score: number;
};

export type LineupHealthMetric = {
  key: keyof typeof LINEUP_HEALTH_WEIGHTS;
  label: string;
  score: number;
  rating: LineupHealthRating;
  detail: string;
  severity: LineupHealthSeverity;
};

export type LineupHealthRecommendation = {
  key: string;
  message: string;
  severity: LineupHealthSeverity;
  impact: number;
};

export type LineupHealthSummary = {
  overallScore: number;
  overallRating: LineupHealthRating;
  metrics: LineupHealthMetric[];
  recommendations: LineupHealthRecommendation[];
  strength: {
    teamAverageEdr: number | null;
    opponentAverageEdr: number | null;
    difference: number | null;
  };
  compatibilityLines: LineupHealthLineCompatibility[];
};

export type BuildLineupHealthInput = {
  positions: LineupHealthPosition[];
  entries: LineupHealthEntry[];
  lineStatuses: LineupHealthLineStatus;
  playersById: Map<string, LineupHealthPlayer>;
  opponentAverageEdr?: number | string | null;
  workspaceId?: string | null;
  partnerPreferences: PartnerPreferenceLike[];
  participationRows: ParticipationReportRow[];
  conflicts: LineupHealthConflict[];
  playerLabel: (playerId: string) => string;
};

const defaultedLineStatuses = new Set(['default_by_opponent', 'default_by_us']);

export function fallbackLineupHealthSummary(): LineupHealthSummary {
  const metrics = [
    metric('strength', 'Strength', 70, 'Health details unavailable.'),
    metric('fairness', 'Fairness', 70, 'Health details unavailable.'),
    metric('partnerCompatibility', 'Partner Compatibility', 70, 'Health details unavailable.'),
    metric('availability', 'Availability', 70, 'Health details unavailable.'),
    metric('scheduling', 'Scheduling', 70, 'Health details unavailable.'),
  ];
  return {
    overallScore: 70,
    overallRating: healthRating(70),
    metrics,
    recommendations: [recommendation('health:fallback', 'Lineup Health could not evaluate this lineup yet.', 'warning', 1)],
    strength: {
      teamAverageEdr: null,
      opponentAverageEdr: null,
      difference: null,
    },
    compatibilityLines: [],
  };
}

export function lineupHealthCollapseStorageKey(userId: string | null | undefined): string {
  return `${LINEUP_HEALTH_COLLAPSE_STORAGE_PREFIX}:${userId || 'anonymous'}`;
}

export function healthRating(score: number): LineupHealthRating {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Needs Attention';
  return 'At Risk';
}

function severityForScore(score: number): LineupHealthSeverity {
  if (score >= 90) return 'positive';
  if (score >= 75) return 'info';
  if (score >= 60) return 'warning';
  return 'error';
}

function metric(key: keyof typeof LINEUP_HEALTH_WEIGHTS, label: string, score: number, detail: string): LineupHealthMetric {
  const rounded = Math.max(0, Math.min(100, Math.round(score)));
  return {
    key,
    label,
    score: rounded,
    rating: healthRating(rounded),
    detail,
    severity: severityForScore(rounded),
  };
}

function recommendation(key: string, message: string, severity: LineupHealthSeverity, impact: number): LineupHealthRecommendation {
  return { key, message, severity, impact };
}

function compatibilityScore(compatibility: PartnerCompatibility): number {
  if (compatibility === 'Excellent') return 100;
  if (compatibility === 'Good') return 88;
  if (compatibility === 'Poor') return 45;
  if (compatibility === 'Unknown') return 76;
  return 76;
}

function scoreStrength(teamAverageEdr: number | null, opponentAverageEdr: number | string | null | undefined): number {
  const difference = calculateEdrDifference(teamAverageEdr, opponentAverageEdr);
  if (teamAverageEdr === null) return 70;
  if (difference === null) return 82;
  if (difference >= 0.25) return 100;
  if (difference >= 0) return 88 + difference * 48;
  return Math.max(35, 88 + difference * 120);
}

function average(values: number[]): number {
  if (values.length === 0) return 100;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function formatDelta(delta: number): string {
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

function activeEntries(input: BuildLineupHealthInput): LineupHealthEntry[] {
  return input.entries.filter((entry) => !defaultedLineStatuses.has(input.lineStatuses[entry.league_configuration_position_id] || 'normal'));
}

export function buildLineupHealth(input: BuildLineupHealthInput): LineupHealthSummary {
  const lineupEntries = activeEntries(input);
  const assignedPlayers = lineupEntries
    .map((entry) => {
      const position = input.positions.find((item) => item.id === entry.league_configuration_position_id);
      const player = input.playersById.get(entry.player_id);
      return player ? { ...player, lineType: position?.line_type || null } : null;
    })
    .filter(Boolean) as Array<LineupHealthPlayer & { lineType: string | null }>;

  const teamAverageEdr = calculateLineupAverageEdr(assignedPlayers);
  const edrDifference = calculateEdrDifference(teamAverageEdr, input.opponentAverageEdr);
  const recommendations: LineupHealthRecommendation[] = [];

  const strengthScore = scoreStrength(teamAverageEdr, input.opponentAverageEdr);
  if (edrDifference != null && edrDifference >= 0.1) {
    recommendations.push(recommendation('strength:positive', 'Strong average EDR.', 'positive', 18));
  } else if (edrDifference != null && edrDifference < -0.15) {
    recommendations.push(recommendation('strength:gap', `Average EDR is ${Math.abs(edrDifference).toFixed(2)} below opponent.`, 'warning', 35));
  }

  const participationByPlayer = new Map(input.participationRows.map((row) => [row.playerId, row]));
  const assignedPlayerIds = [...new Set(lineupEntries.map((entry) => entry.player_id))];
  const fairnessIssues = assignedPlayerIds
    .map((playerId) => participationByPlayer.get(playerId))
    .filter(Boolean) as ParticipationReportRow[];
  const fairnessPenalty = fairnessIssues.reduce((sum, row) => {
    const delta = Math.abs(row.statusDelta);
    return sum + (delta <= 1 ? 0 : delta === 2 ? 18 : 30 + (delta - 3) * 8);
  }, 0);
  const fairnessScore = Math.max(30, 100 - fairnessPenalty);
  const attentionRows = fairnessIssues.filter((row) => Math.abs(row.statusDelta) > 1);
  if (attentionRows.length === 0 && assignedPlayerIds.length > 0) {
    recommendations.push(recommendation('fairness:maintained', 'Fairness maintained.', 'positive', 16));
  }
  attentionRows.forEach((row) => {
    const direction = row.statusDelta < 0 ? 'below' : 'above';
    recommendations.push(recommendation(
      `fairness:${row.playerId}`,
      `${input.playerLabel(row.playerId)} projected ${Math.abs(row.statusDelta)} match${Math.abs(row.statusDelta) === 1 ? '' : 'es'} ${direction} target.`,
      Math.abs(row.statusDelta) >= 3 ? 'error' : 'warning',
      45 + Math.abs(row.statusDelta),
    ));
  });

  const compatibilityLines = input.positions.flatMap<LineupHealthLineCompatibility>((position) => {
    if (defaultedLineStatuses.has(input.lineStatuses[position.id] || 'normal') || position.line_type === 'singles' || position.required_player_count < 2) return [];
    const lineEntries = lineupEntries
      .filter((entry) => entry.league_configuration_position_id === position.id)
      .sort((a, b) => a.slot_number - b.slot_number);
    if (lineEntries.length < 2 || !input.workspaceId) return [];
    if (!lineEntries[0].player_id || !lineEntries[1].player_id || lineEntries[0].player_id === lineEntries[1].player_id) {
      return [{ positionCode: position.position_code, compatibility: 'Unknown', score: compatibilityScore('Unknown') }];
    }
    const preference = partnerPreferenceForPair(input.partnerPreferences, input.workspaceId, lineEntries[0].player_id, lineEntries[1].player_id);
    const compatibility = normalizeCompatibility(preference?.compatibility);
    return [{ positionCode: position.position_code, compatibility, score: compatibilityScore(compatibility) }];
  });
  const compatibilityOverallScore = average(compatibilityLines.map((line) => line.score));
  compatibilityLines.forEach((line) => {
    if (line.compatibility === 'Excellent') {
      recommendations.push(recommendation(`compatibility:${line.positionCode}`, `Excellent ${line.positionCode} pairing.`, 'positive', 14));
    } else if (line.compatibility === 'Poor') {
      recommendations.push(recommendation(
        `compatibility:${line.positionCode}`,
        `${line.positionCode} partner compatibility is Poor.`,
        'warning',
        48,
      ));
    }
  });

  const availabilityCounts = assignedPlayers.reduce<Record<string, number>>((acc, player) => {
    const status = player.availability_status || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const availabilityScore = Math.max(0, 100 - (availabilityCounts.maybe || 0) * 12 - (availabilityCounts.unknown || 0) * 16 - (availabilityCounts.not_available || 0) * 45);
  assignedPlayers.forEach((player) => {
    const status = player.availability_status || 'unknown';
    if (status === 'maybe') recommendations.push(recommendation(`availability:maybe:${player.id}`, `${input.playerLabel(player.id)} marked Maybe.`, 'warning', 38));
    if (status === 'unknown') recommendations.push(recommendation(`availability:unknown:${player.id}`, `${input.playerLabel(player.id)} availability unknown.`, 'warning', 34));
    if (status === 'not_available') recommendations.push(recommendation(`availability:out:${player.id}`, `${input.playerLabel(player.id)} unavailable.`, 'error', 90));
  });

  const conflictPlayerIds = new Set(input.conflicts.map((conflict) => conflict.player_id));
  const activeConflictIds = assignedPlayerIds.filter((playerId) => conflictPlayerIds.has(playerId));
  const lineupPlayerIds = lineupEntries.map((entry) => entry.player_id);
  const duplicateIds = lineupPlayerIds.filter((playerId, index, all) => all.indexOf(playerId) !== index);
  const schedulingScore = Math.max(0, 100 - activeConflictIds.length * 35 - duplicateIds.length * 45);
  activeConflictIds.forEach((playerId) => {
    recommendations.push(recommendation(`schedule:conflict:${playerId}`, `${input.playerLabel(playerId)} assigned to another match today.`, 'error', 75));
  });
  [...new Set(duplicateIds)].forEach((playerId) => {
    recommendations.push(recommendation(`schedule:duplicate:${playerId}`, `${input.playerLabel(playerId)} assigned more than once in this lineup.`, 'error', 85));
  });

  const metrics = [
    metric('strength', 'Strength', strengthScore, edrDifference == null ? 'Average EDR available; opponent comparison incomplete.' : `Projected difference ${formatDelta(Number(edrDifference.toFixed(2)))}.`),
    metric('fairness', 'Fairness', fairnessScore, attentionRows.length === 0 ? 'Everyone projected within one match of target.' : `${attentionRows.length} player${attentionRows.length === 1 ? '' : 's'} need attention.`),
    metric('partnerCompatibility', 'Partner Compatibility', compatibilityOverallScore, compatibilityLines.length === 0 ? 'No complete doubles pairings yet.' : `${compatibilityLines.map((line) => `${line.positionCode}: ${line.compatibility}`).join(' • ')}.`),
    metric('availability', 'Availability', availabilityScore, availabilityScore >= 90 ? 'All assigned players confirmed or acceptable.' : `${(availabilityCounts.maybe || 0) + (availabilityCounts.unknown || 0) + (availabilityCounts.not_available || 0)} player${(availabilityCounts.maybe || 0) + (availabilityCounts.unknown || 0) + (availabilityCounts.not_available || 0) === 1 ? '' : 's'} need availability review.`),
    metric('scheduling', 'Scheduling', schedulingScore, activeConflictIds.length === 0 && duplicateIds.length === 0 ? 'No scheduling conflicts.' : `${activeConflictIds.length + duplicateIds.length} conflict${activeConflictIds.length + duplicateIds.length === 1 ? '' : 's'} detected.`),
  ];
  const overallScore = Math.round(metrics.reduce((sum, item) => sum + item.score * LINEUP_HEALTH_WEIGHTS[item.key], 0));
  const severityRank: Record<LineupHealthSeverity, number> = { error: 0, warning: 1, info: 2, positive: 3 };
  const orderedRecommendations = recommendations
    .sort((a, b) => severityRank[a.severity] - severityRank[b.severity] || b.impact - a.impact || a.message.localeCompare(b.message, undefined, { sensitivity: 'base' }))
    .slice(0, 5);

  return {
    overallScore,
    overallRating: healthRating(overallScore),
    metrics,
    recommendations: orderedRecommendations,
    strength: {
      teamAverageEdr,
      opponentAverageEdr: input.opponentAverageEdr == null || input.opponentAverageEdr === '' ? null : Number(input.opponentAverageEdr),
      difference: edrDifference,
    },
    compatibilityLines,
  };
}
